/**
 * Serialize a Date to a timezone-naive ISO 8601 string (local time).
 * Use instead of Date.toISOString() (which always produces UTC with Z suffix).
 * e.g. new Date() → "2026-03-15T08:30:00"
 */
export function toLocalISOString(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * Format the elapsed time between two date strings as e.g. "1h 2m 3s", "4m 10s" or "45s".
 * Returns '-' when either date is missing or invalid.
 */
export function formatDuration(start?: string | null, end?: string | null): string {
  if (!start || !end) return '-';

  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (isNaN(ms) || ms < 0) return '-';

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return parts.join(' ');
}

/**
 * Parse a typed time of day: "1400", "700", "7", "7:30", "07.30", "7pm", "12:30 AM", ...
 * Hour with optional 2-digit minutes (":" / "." separator optional) and optional am/pm.
 * Returns null for anything else or out of range (24h: 0-23; am/pm: 1-12; minutes 0-59).
 */
export function parseTypedTime(text: string): { hours: number; minutes: number } | null {
  const match = /^\s*(\d{1,2})(?:[:.]?(\d{2}))?\s*([ap]m)?\s*$/i.exec(text);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toLowerCase();
  if (minutes > 59) return null;
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem === 'pm' ? 12 : 0);
  } else if (hours > 23) {
    return null;
  }
  return { hours, minutes };
}
